import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lightning, CheckCircle, CarProfile, Phone, ArrowLeft, Wallet, Info, PhoneCall, Handshake,
} from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { AddressInput } from "./AddressInput";
import { SquarePayment } from "./SquarePayment";
import { ReceiptOptions } from "./ReceiptOptions";
import { QUICK_SERVICES, PHONE, PHONE_RAW } from "../data";
import { squareEnabled } from "../lib/square";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["ASAP", "In 30 min", "In 1 hour", "Tonight", "Schedule"];

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
function computeWhen(slot) {
  const d = new Date();
  if (slot === "In 30 min") d.setMinutes(d.getMinutes() + 30);
  else if (slot === "In 1 hour") d.setHours(d.getHours() + 1);
  else if (slot === "Tonight") d.setHours(20, 0, 0, 0);
  return { date: fmtDate(d), time: fmtTime(d) };
}

const EMPTY = { service_type: "", name: "", phone: "", email: "", pickup: "", dropoff: "", pickupPlaceId: null, dropoffPlaceId: null, date: "", time: "", notes: "" };
const inputCls = "border-2 border-ink rounded-none h-11 bg-white text-ink focus-visible:ring-2 focus-visible:ring-brandblue";

// Premium "we're reviewing your request" note shown after any booking/quote.
const ReviewNote = () => (
  <div data-testid="review-note" className="mt-6 w-full relative overflow-hidden bg-ink text-white border-2 border-ink shadow-hard-sm">
    <div className="absolute left-0 top-0 h-full w-1.5 bg-sun" />
    <div className="p-5 pl-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-sun">We're on it</p>
      <p className="mt-1.5 font-display font-extrabold text-lg leading-tight tracking-tight">
        We're reviewing your request.
      </p>
      <p className="mt-2 text-sm leading-relaxed text-white/70">
        A member of our team will call you as soon as possible with the accurate ETA. Prefer to know right away?
      </p>
      <a data-testid="review-note-call" href={`tel:${PHONE_RAW}`}
        className="mt-4 inline-flex items-center gap-2 bg-sun text-ink font-bold text-sm px-4 py-2.5 border-2 border-sun hover:bg-transparent hover:text-sun transition-colors">
        <PhoneCall weight="fill" size={16} /> Call {PHONE}
      </a>
    </div>
  </div>
);

export const BookingWidget = () => {
  const [f, setF] = useState(EMPTY);
  const [slot, setSlot] = useState("ASAP");
  const [step, setStep] = useState("form"); // form | price | done_quote | done_pay
  const [fare, setFare] = useState(null); // { fare_id, price }
  const [payMethod, setPayMethod] = useState(null); // "paid" | "later"
  const [showCard, setShowCard] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [tipPct, setTipPct] = useState(15);   // 15/30/50 or "other" (default 15%)
  const [customTip, setCustomTip] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const reset = () => { setF(EMPTY); setSlot("ASAP"); setStep("form"); setFare(null); setPayMethod(null); setShowCard(false); setShowDisclaimer(false); setTipPct(15); setCustomTip(""); };

  const { user } = useAuth();
  useEffect(() => {
    // "Book again" payload from My Trips
    const raw = localStorage.getItem("ds_rebook");
    if (raw) {
      try {
        const r = JSON.parse(raw);
        setF((s) => ({ ...s, service_type: r.service_type || s.service_type, pickup: r.pickup || s.pickup, dropoff: r.dropoff || s.dropoff, notes: r.notes || s.notes, pickupPlaceId: null, dropoffPlaceId: null }));
      } catch { /* ignore */ }
      localStorage.removeItem("ds_rebook");
    }
  }, []);
  useEffect(() => {
    if (!user) return;
    setF((s) => ({
      ...s,
      name: s.name || user.name || "",
      phone: s.phone || user.phone || "",
      email: s.email || user.email || "",
      pickup: s.pickup || user.home_address || "",
      notes: s.notes || user.driver_notes || "",
    }));
  }, [user]);

  const buildWhen = () => (slot === "Schedule" ? { date: f.date, time: f.time } : computeWhen(slot));
  const buildNotes = () => `${f.notes ? f.notes + " · " : ""}Pickup slot: ${slot} · via website widget`;

  // Primary: compute the price for the trip, then show payment options
  const getPrice = async () => {
    if (!f.service_type || !f.name.trim() || !f.phone.trim()) {
      toast.error("Add your name, phone and the service first.");
      return;
    }
    if (!f.pickup.trim() || !f.dropoff.trim()) {
      toast.error("Enter a pickup and drop-off address to get your price.");
      return;
    }
    if (slot === "Schedule" && (!f.date || !f.time)) {
      toast.error("Pick a date and time for your scheduled trip.");
      return;
    }
    const when = buildWhen();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/fare/quote`, {
        name: f.name, phone: f.phone, email: f.email || null,
        service_type: f.service_type, pickup: f.pickup, dropoff: f.dropoff,
        pickup_place_id: f.pickupPlaceId, dropoff_place_id: f.dropoffPlaceId,
        date: when.date, time: when.time, notes: buildNotes(),
      });
      setFare(data);
      setStep("price");
      setShowDisclaimer(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't price that trip. Please check the addresses.");
    } finally {
      setLoading(false);
    }
  };

  // Secondary: send a human quote request (no auto price)
  const sendQuote = async () => {
    if (!f.service_type || !f.name.trim() || !f.phone.trim()) {
      toast.error("Add your name, phone and the service first.");
      return;
    }
    const when = buildWhen();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        kind: "quote", service_type: f.service_type, name: f.name, phone: f.phone,
        email: f.email || undefined, pickup: f.pickup, dropoff: f.dropoff,
        date: when.date, time: when.time, notes: `QUOTE REQUEST · ${buildNotes()}`,
      });
      setStep("done_quote");
      toast.success("Quote request sent! We'll reply shortly.");
    } catch {
      toast.error("Couldn't send — please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  const payLater = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/payments/pay-later`, { fare_id: fare.fare_id });
      setPayMethod("later");
      setStep("done_pay");
      toast.success("Booked! Pay your driver on pickup.");
    } catch {
      toast.error("Couldn't confirm — please call us.");
    } finally {
      setLoading(false);
    }
  };

  const payNow = async (sourceId) => {
    setLoading(true);
    try {
      const payload = {
        fare_id: fare.fare_id, source_id: sourceId,
        idempotency_key: (crypto.randomUUID?.() || String(Date.now())),
      };
      if (tipPct === "other") payload.tip_amount = tipAmount;
      else payload.tip_percent = tipPct;
      const { data } = await axios.post(`${API}/payments/square`, payload);
      setFare((s) => ({ ...s, grand_total: data.total, tip: data.tip }));
      setPayMethod("paid");
      setStep("done_pay");
      toast.success("Payment received — you're all set!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed. Try again or pay on pickup.");
    } finally {
      setLoading(false);
    }
  };

  // saved Home/Work places for one-tap fill
  const savedPlaces = user
    ? [
        user.home_address && { label: "Home", val: user.home_address },
        user.work_address && { label: "Work", val: user.work_address },
      ].filter(Boolean)
    : [];

  // tip is charged on the pre-tax subtotal (fare + night surcharge), or a custom amount
  const presetTip = fare && typeof tipPct === "number"
    ? Math.round(((fare.price + (fare.night_surcharge || 0)) * tipPct / 100) * 100) / 100 : 0;
  const tipAmount = tipPct === "other"
    ? Math.round((parseFloat(customTip) || 0) * 100) / 100 : presetTip;
  const chargeTotal = fare ? Math.round((fare.total + tipAmount) * 100) / 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="booking-widget"
      className="w-full bg-white border-2 border-ink shadow-[10px_10px_0_0_#FFDE00] p-6 sm:p-7"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 bg-sun border-2 border-ink flex items-center justify-center">
          <Lightning weight="fill" size={16} className="text-ink" />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/70">Instant booking</p>
      </div>

      <a
        href={`tel:${PHONE_RAW}`}
        data-testid="widget-call"
        className="flex items-center justify-between gap-3 bg-ink text-white border-2 border-ink px-3.5 py-2.5 hover:bg-sun hover:text-ink transition-colors group"
      >
        <span className="flex items-center gap-2">
          <Phone weight="fill" size={18} className="text-sun group-hover:text-ink" />
          <span className="text-[11px] font-bold uppercase tracking-[0.15em]">Need help? Call us</span>
        </span>
        <span className="font-display font-extrabold text-base tracking-tight">{PHONE}</span>
      </a>

      {/* STEP: FORM */}
      {step === "form" && (
        <div data-testid="widget-form" className="space-y-3 mt-4">
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-ink leading-none">
            Book your ride in seconds.
          </h3>
          <Select value={f.service_type} onValueChange={set("service_type")}>
            <SelectTrigger data-testid="widget-service" className={inputCls}>
              <div className="flex items-center gap-2"><CarProfile weight="fill" size={16} /><SelectValue placeholder="What do you need?" /></div>
            </SelectTrigger>
            <SelectContent className="bg-white border-2 border-ink rounded-none">
              {QUICK_SERVICES.map((s) => (<SelectItem key={s} value={s} data-testid={`widget-service-${s}`}>{s}</SelectItem>))}
            </SelectContent>
          </Select>

          <Input data-testid="widget-name" value={f.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
          <Input data-testid="widget-phone" value={f.phone} onChange={set("phone")} placeholder="Phone (with area code, e.g. 902…)" className={inputCls} />
          <Input data-testid="widget-email" type="email" value={f.email} onChange={set("email")} placeholder="Email (for your receipt)" className={inputCls} />

          <AddressInput testid="widget-pickup" value={f.pickup} onChange={set("pickup")} onPlace={(id) => setF((s) => ({ ...s, pickupPlaceId: id }))} placeholder="Pickup address" className={inputCls} />
          {savedPlaces.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap -mt-1">
              <span className="text-[10px] text-ink/40 uppercase tracking-wider">Pickup:</span>
              {savedPlaces.map((pl) => (
                <button key={pl.label} type="button" data-testid={`pickup-place-${pl.label}`}
                  onClick={() => setF((s) => ({ ...s, pickup: pl.val, pickupPlaceId: null }))}
                  className="text-[11px] font-bold border border-ink/30 px-2.5 py-1 hover:bg-sun transition-colors">{pl.label}</button>
              ))}
            </div>
          )}
          <AddressInput testid="widget-dropoff" value={f.dropoff} onChange={set("dropoff")} onPlace={(id) => setF((s) => ({ ...s, dropoffPlaceId: id }))} placeholder="Drop-off address" className={inputCls} />
          {savedPlaces.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap -mt-1">
              <span className="text-[10px] text-ink/40 uppercase tracking-wider">Drop-off:</span>
              {savedPlaces.map((pl) => (
                <button key={pl.label} type="button" data-testid={`dropoff-place-${pl.label}`}
                  onClick={() => setF((s) => ({ ...s, dropoff: pl.val, dropoffPlaceId: null }))}
                  className="text-[11px] font-bold border border-ink/30 px-2.5 py-1 hover:bg-sun transition-colors">{pl.label}</button>
              ))}
            </div>
          )}

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50 mb-2">Pickup time</p>
            <div className="flex flex-wrap gap-2">
              {SLOTS.map((s) => (
                <button key={s} type="button" data-testid={`slot-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setSlot(s)}
                  className={`px-3 py-2 text-sm font-bold border-2 border-ink transition-colors ${slot === s ? "bg-ink text-sun" : "bg-white text-ink hover:bg-sun"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {slot === "Schedule" && (
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="widget-date" type="date" value={f.date} onChange={set("date")} className={inputCls} />
              <Input data-testid="widget-time" type="time" value={f.time} onChange={set("time")} className={inputCls} />
            </div>
          )}

          <Textarea data-testid="widget-notes" value={f.notes} onChange={set("notes")} rows={2}
            placeholder="Special instructions (if any) — flight #, gate code, items…"
            className="border-2 border-ink rounded-none focus-visible:ring-2 focus-visible:ring-brandblue" />

          {/* Two big buttons */}
          <button data-testid="widget-book-btn" type="button" onClick={getPrice} disabled={loading}
            className="w-full bg-sun text-ink font-bold text-base py-3.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-150 disabled:opacity-60">
            {loading ? "Getting price…" : "Book a ride — see price"}
          </button>
          <button data-testid="widget-quote-btn" type="button" onClick={sendQuote} disabled={loading}
            className="w-full bg-ink text-white font-bold text-base py-3.5 border-2 border-ink hover:bg-brandblue transition-colors disabled:opacity-60">
            Looking for a quote?
          </button>
        </div>
      )}

      {/* STEP: PRICE + PAYMENT */}
      {step === "price" && fare && (
        <div data-testid="widget-price" className="mt-4">
          <button data-testid="widget-back" onClick={() => { setStep("form"); setShowCard(false); }}
            className="flex items-center gap-1 text-xs font-bold text-ink/60 hover:text-ink mb-3">
            <ArrowLeft size={14} /> Edit trip
          </button>
          <div className="bg-ink text-white border-2 border-ink p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sun text-center">Your price</p>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-white/70">Fare</span><span className="font-semibold">${Number(fare.price).toFixed(2)}</span></div>
              {fare.night_surcharge > 0 && (
                <div className="flex justify-between" data-testid="night-surcharge-row">
                  <span className="text-white/70">Night surcharge (11PM–6AM)</span>
                  <span className="font-semibold">${Number(fare.night_surcharge).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-white/70">Tax ({fare.tax_percent}%)</span><span className="font-semibold">${Number(fare.tax).toFixed(2)}</span></div>
            </div>
            <div className="border-t-2 border-white/20 mt-3 pt-3 flex items-end justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Total (CAD)</span>
              <span data-testid="fare-price" className="font-display font-extrabold text-4xl tracking-tighter">${Number(fare.total).toFixed(2)}</span>
            </div>
            <p data-testid="fare-approx-note" className="mt-3 pt-3 border-t border-white/10 text-[11px] leading-relaxed text-white/55 flex items-start gap-1.5">
              <Info weight="fill" size={13} className="text-sun mt-[1px] shrink-0" />
              <span>This is an <span className="text-white/80 font-semibold">approximate price</span>. The final cost may vary slightly as the trip goes.</span>
            </p>
          </div>

          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50 mt-4 mb-2">How would you like to pay?</p>

          {!showCard && (
            <div className="space-y-2.5">
              <button data-testid="choose-paylater-btn" type="button" onClick={payLater} disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-sun text-ink font-bold py-3.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
                <Wallet weight="fill" size={20} /> Pay cash on pickup
              </button>
              {squareEnabled() ? (
                <button data-testid="choose-paynow-btn" type="button" onClick={() => setShowCard(true)} disabled={loading}
                  className="w-full bg-brandblue text-white font-bold py-3.5 border-2 border-ink hover:brightness-110 transition-all disabled:opacity-60">
                  Pay now — card, Apple Pay or Google Pay
                </button>
              ) : (
                <p className="text-[11px] text-ink/45 text-center">Card payment is being set up — choose Pay on pickup for now.</p>
              )}
            </div>
          )}

          {showCard && (
            <div className="mt-1">
              {/* Tip selector */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50">Add a tip?</p>
                <span data-testid="tip-nudge" className="text-[11px] font-bold text-ink/60">Most riders tip 15% ❤</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {[15, 30, 50].map((p) => (
                  <button key={p} type="button" data-testid={`tip-${p}`} onClick={() => setTipPct(p)}
                    className={`relative py-2.5 text-sm font-bold border-2 border-ink transition-colors ${tipPct === p ? "bg-ink text-sun" : "bg-white text-ink hover:bg-sun"}`}>
                    {`${p}%`}
                    {p === 15 && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-sun text-ink text-[8px] font-black uppercase tracking-wide px-1.5 py-0.5 border border-ink whitespace-nowrap">Popular</span>
                    )}
                  </button>
                ))}
              </div>
              <button type="button" data-testid="tip-other" onClick={() => setTipPct("other")}
                className={`w-full py-2.5 text-sm font-bold border-2 border-ink transition-colors mb-2 ${tipPct === "other" ? "bg-ink text-sun" : "bg-white text-ink hover:bg-sun"}`}>
                Other amount (enter 0 for no tip)
              </button>
              {tipPct === "other" && (
                <div className="relative mb-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-ink">$</span>
                  <Input data-testid="tip-custom-input" type="number" min="0" step="0.50" inputMode="decimal"
                    value={customTip} onChange={(e) => setCustomTip(e.target.value)}
                    placeholder="Enter tip amount"
                    className="border-2 border-ink rounded-none h-11 bg-white pl-7" />
                </div>
              )}
              {tipAmount > 0 && (
                <div className="flex justify-between text-sm text-ink/70 mb-3" data-testid="tip-summary">
                  <span>Tip{tipPct !== "other" ? ` (${tipPct}%)` : ""}</span>
                  <span className="font-bold">+${tipAmount.toFixed(2)} · Total ${chargeTotal.toFixed(2)}</span>
                </div>
              )}
              <SquarePayment amount={chargeTotal} onToken={payNow} busy={loading} />
              <button data-testid="cancel-card-btn" type="button" onClick={() => { setShowCard(false); setTipPct(15); setCustomTip(""); }}
                className="w-full mt-2 text-xs font-bold text-ink/50 hover:text-ink">
                ← Back to payment options
              </button>
            </div>
          )}
        </div>
      )}

      {/* STEP: DONE (paid or pay-later) */}
      {step === "done_pay" && (
        <div data-testid="widget-done-pay" className="py-8 flex flex-col items-start">
          <CheckCircle weight="fill" size={48} className="text-brandblue" />
          <h3 className="font-display font-extrabold text-2xl tracking-tight mt-3 text-ink">
            {payMethod === "paid" ? "Paid — you're all set!" : "Booked! See you soon."}
          </h3>
          <p className="mt-2 text-ink/60 text-sm leading-relaxed">
            {payMethod === "paid"
              ? `Thanks ${f.name.split(" ")[0]} — we've received your $${Number(fare?.grand_total ?? fare?.total).toFixed(2)} payment and your driver is on the way.`
              : `Thanks ${f.name.split(" ")[0]} — your ride is booked. Please have $${Number(fare?.total).toFixed(2)} ready for pickup.`}
          </p>
          <ReviewNote />
          <ReceiptOptions fare={fare} defaultEmail={f.email} defaultPhone={f.phone} />
          <button data-testid="widget-book-again" onClick={reset}
            className="mt-5 bg-ink text-white font-bold px-5 py-2.5 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">
            Book another
          </button>
        </div>
      )}

      {/* STEP: DONE (quote) */}
      {step === "done_quote" && (
        <div data-testid="widget-done-quote" className="py-8 flex flex-col items-start">
          <CheckCircle weight="fill" size={48} className="text-brandblue" />
          <h3 className="font-display font-extrabold text-2xl tracking-tight mt-3 text-ink">Quote request sent!</h3>
          <p className="mt-2 text-ink/60 text-sm leading-relaxed">
            Thanks {f.name.split(" ")[0]} — we'll text or call you with a price shortly.
          </p>
          <ReviewNote />
          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="widget-quote-again" onClick={reset}
              className="bg-ink text-white font-bold px-5 py-2.5 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">
              Start over
            </button>
          </div>
        </div>
      )}

      {/* Estimated-price disclosure — shown on every online booking */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent data-testid="estimate-disclaimer" className="rounded-none border-2 border-ink p-0 max-w-md gap-0 overflow-hidden">
          <div className="bg-ink px-6 py-5">
            <DialogHeader className="space-y-0">
              <span className="inline-flex items-center gap-2 text-sun text-[10px] font-bold uppercase tracking-[0.25em]">
                <Handshake weight="fill" size={16} /> Fair &amp; friendly
              </span>
              <DialogTitle className="mt-2 text-left font-display font-extrabold text-2xl tracking-tight text-white">
                Your price is an estimate
              </DialogTitle>
              <DialogDescription className="sr-only">
                The price shown is an estimate. Call us to confirm if it doesn't feel right, and note the final fare may differ slightly.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-5 space-y-3.5 text-sm leading-relaxed text-ink/75">
            <p>
              The amount shown is our best estimate for your trip, based on the route and distance — for
              <span className="font-semibold text-ink"> every service we offer</span>.
            </p>
            <p>
              If anything doesn't look quite right for the ride or delivery you're booking, we'd genuinely love to hear from you —
              just give us a quick call and we'll happily confirm the details before you go.
            </p>
            <p>
              On the odd occasion the final fare may differ a little (say, a route change, an extra stop, or wait time), and your driver
              will always let you know upfront. We're committed to keeping things clear, honest and fair — every single time.
            </p>
          </div>
          <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2.5">
            <a data-testid="disclaimer-call" href={`tel:${PHONE_RAW}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-ink font-bold text-sm px-4 py-3 border-2 border-ink hover:bg-sun transition-colors">
              <PhoneCall weight="fill" size={16} /> Call to confirm
            </a>
            <button data-testid="disclaimer-continue" type="button" onClick={() => setShowDisclaimer(false)}
              className="flex-1 bg-sun text-ink font-bold text-sm px-4 py-3 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">
              Got it — continue
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
